import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import "./dashboard.css";
import { useDispatch, useSelector } from "react-redux";
import { getDashboardStats } from "../../Redux/Actions/dashboardAction";
import { canEditCms } from "../../utils/accessControl";
import { useContextUserId } from "../../hooks/useSupportContext";
import toast from "react-hot-toast";
import {
  ArrowRight,
  BookOpen,
  FileText,
  ListVideo,
  Mail,
  Monitor,
  Play,
  Plus,
} from "lucide-react";

const SUPPORT_EMAIL = "Jesper@notiz.se";
/** Set when ready — wire into helpLinks below */
const KNOWLEDGE_BASE_URL = "";
const VIDEO_GUIDES_URL = "";

function StatValue({ loading, value }) {
  if (loading) return <span className="dash-stat-loader" aria-hidden="true" />;
  return (
    <>
      <span className="dash-stat-number">{value ?? 0}</span>
      <span className="dash-stat-unit">pcs</span>
    </>
  );
}

function Dashboard({ user, socketRef }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canEdit = canEditCms(user);
  const contextUserId = useContextUserId();
  const { content_stats, contentLoading } = useSelector((state) => state.Content);
  const { isPlaylistCountLoading, playlistCount } = useSelector((state) => state.Playlist);
  const { screen_stats, statsLoading } = useSelector((state) => state.Screen);

  useEffect(() => {
    if (!contextUserId) return;
    socketRef.current?.emit("leaving--connection");
    dispatch(getDashboardStats(contextUserId));
  }, [contextUserId, dispatch, socketRef]);

  const displayName = user?.username || user?.name || "there";

  const screenStatus = useMemo(() => {
    const total = screen_stats?.total ?? 0;
    const online = screen_stats?.online ?? 0;
    if (statsLoading) return { text: "Checking screen status…", online: true };
    if (total === 0) return { text: "No screens added yet", online: true };
    if (online === total) return { text: "All screens are online", online: true };
    if (online === 0) return { text: "All screens are offline", online: false };
    return { text: `${online} of ${total} screens online`, online: false };
  }, [screen_stats, statsLoading]);

  const quickActions = [
    {
      key: "content",
      label: "Create content",
      icon: Plus,
      tone: "teal",
      path: "/content",
    },
    {
      key: "playlist",
      label: "Create playlist",
      icon: ListVideo,
      tone: "purple",
      path: "/playlist",
    },
    {
      key: "screen",
      label: "Add screen",
      icon: Monitor,
      tone: "bronze",
      path: "/screen",
    },
  ];

  const helpLinks = [
    {
      key: "kb",
      label: "Knowledge base",
      icon: BookOpen,
      tone: "teal",
      href: KNOWLEDGE_BASE_URL,
      action: KNOWLEDGE_BASE_URL ? "link" : "coming-soon",
    },
    {
      key: "video",
      label: "Video guides",
      icon: Play,
      tone: "purple",
      href: VIDEO_GUIDES_URL,
      action: VIDEO_GUIDES_URL ? "link" : "coming-soon",
    },
    {
      key: "support",
      label: "Contact support",
      icon: Mail,
      tone: "bronze",
      action: "support",
    },
  ];

  const handleComingSoon = (label) => {
    toast(`${label} — link coming soon`, { icon: "ℹ️" });
  };

  const handleContactSupport = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      toast.success(`Support email copied: ${SUPPORT_EMAIL}`);
    } catch {
      toast.success(`Email us at ${SUPPORT_EMAIL}`);
    }
    window.location.href = `mailto:${SUPPORT_EMAIL}`;
  };

  return (
    <Layout title="Dashboard" user={user}>
      <div className="dash-page">
        <header className="dash-hero">
          <h1 className="dash-welcome">
            Welcome back, {displayName} <span aria-hidden="true">👋</span>
          </h1>
          <p className={`dash-status${screenStatus.online ? " dash-status--ok" : " dash-status--warn"}`}>
            <span className="dash-status-dot" aria-hidden="true" />
            {screenStatus.text}
          </p>
        </header>

        <section className="dash-stat-row" aria-label="Overview">
          <article className="dash-stat-card dash-stat-card--teal">
            <div className="dash-stat-icon-wrap">
              <Monitor size={22} strokeWidth={2} />
            </div>
            <p className="dash-stat-label">Screens</p>
            <p className="dash-stat-value">
              <StatValue loading={statsLoading} value={screen_stats?.total} />
            </p>
            <div className="dash-stat-footer">
              Online: {statsLoading ? "…" : screen_stats?.online ?? 0}
            </div>
          </article>

          <article className="dash-stat-card dash-stat-card--purple">
            <div className="dash-stat-icon-wrap">
              <ListVideo size={22} strokeWidth={2} />
            </div>
            <p className="dash-stat-label">Playlists</p>
            <p className="dash-stat-value">
              <StatValue loading={isPlaylistCountLoading} value={playlistCount} />
            </p>
          </article>

          <article className="dash-stat-card dash-stat-card--bronze">
            <div className="dash-stat-icon-wrap">
              <FileText size={22} strokeWidth={2} />
            </div>
            <p className="dash-stat-label">Content</p>
            <p className="dash-stat-value">
              <StatValue loading={contentLoading} value={content_stats?.total} />
            </p>
          </article>
        </section>

        {canEdit && (
          <section className="dash-section">
            <h2 className="dash-section-title">Quick actions</h2>
            <div className="dash-action-grid">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.key}
                    type="button"
                    className={`dash-action-card dash-action-card--${action.tone}`}
                    onClick={() => navigate(action.path)}
                  >
                    <span className="dash-action-icon">
                      <Icon size={18} strokeWidth={2.5} />
                    </span>
                    <span className="dash-action-label">
                      {action.label}
                      <ArrowRight size={14} className="dash-action-arrow" aria-hidden="true" />
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <section className="dash-section">
          <h2 className="dash-section-title">Help &amp; support</h2>
          <div className="dash-action-grid">
            {helpLinks.map((link) => {
              const Icon = link.icon;
              const cardClass = `dash-action-card dash-action-card--${link.tone} dash-help-card`;
              const cardInner = (
                <>
                  <span className="dash-help-icon">
                    <Icon size={20} strokeWidth={2} />
                  </span>
                  <span className="dash-action-label">
                    {link.label}
                    <ArrowRight size={14} className="dash-action-arrow" aria-hidden="true" />
                  </span>
                </>
              );

              if (link.action === "support") {
                return (
                  <button
                    key={link.key}
                    type="button"
                    className={cardClass}
                    onClick={handleContactSupport}
                  >
                    {cardInner}
                  </button>
                );
              }

              if (link.action === "coming-soon") {
                return (
                  <button
                    key={link.key}
                    type="button"
                    className={`${cardClass} dash-help-card--pending`}
                    onClick={() => handleComingSoon(link.label)}
                  >
                    {cardInner}
                  </button>
                );
              }

              return (
                <a
                  key={link.key}
                  className={cardClass}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {cardInner}
                </a>
              );
            })}
          </div>
        </section>
      </div>
    </Layout>
  );
}

export default Dashboard;
